import React, { type MouseEvent } from 'react';
import { styled, css } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { type ButtonProps } from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import { Box, xcss, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';

const goButtonSelectorName = 'atlassian-intelligence.ui.search-field.shortcut-buttons.go-button';
export const GO_BUTTON_SELECTOR = `[data-component-selector="${goButtonSelectorName}"]`;

type Props = {
	backButtonLabel?: string;
	isLoading: boolean;
	isSubmitDisabled?: boolean;
	onBack?: (e: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
	onSubmit: (e: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
};

const goButtonContentWrapperStyles = xcss({
	display: 'inline-flex',
	minHeight: token('space.300'),
	alignItems: 'center',
	gap: 'space.050',
});

export const ShortcutButtons = ({
	backButtonLabel,
	isLoading,
	isSubmitDisabled,
	onSubmit,
	onBack,
}: Props) => {
	const { formatMessage } = useIntl();
	return (
		<>
			{!isLoading && (
				<GoButton
					spacing="none"
					appearance="primary"
					isDisabled={isSubmitDisabled}
					onClick={onSubmit}
					testId="atlassian-intelligence.ui.search-field.shortcut-buttons.go-button"
					data-component-selector={goButtonSelectorName}
				>
					<Box as="span" paddingInline="space.300" xcss={goButtonContentWrapperStyles}>
						{formatMessage(messages.go)}
						<Text size="small" weight="bold" color="inherit">
							⏎
						</Text>
					</Box>
				</GoButton>
			)}
			{onBack && (
				<BackButton
					appearance="subtle"
					spacing="none"
					iconAfter={
						<CrossIcon
							LEGACY_size="medium"
							spacing="spacious"
							label={backButtonLabel ?? formatMessage(messages.back)}
						/>
					}
					onClick={onBack}
					testId="atlassian-intelligence.ui.search-field.shortcut-buttons.back-button"
				/>
			)}
		</>
	);
};

const buttonStyles = css({
	alignSelf: 'center',
	height: 'auto',
	marginLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const GoButton = styled<ButtonProps>(Button)(buttonStyles);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const BackButton = styled<ButtonProps>(Button)(buttonStyles);
